/**
 * Mock investment account data for the visualise use case and home hero.
 * Returns locale-aware data: GBP / Stocks & Shares ISA for English, USD /
 * Brokerage for US English, JPY / 新NISA for Japanese, KRW / 성장형 펀드 for
 * Korean, CNY / 增长型基金 for Simplified Chinese, and TWD / 成長型基金 for
 * Traditional Chinese. All share the same 149.52% gain trajectory, jagged
 * contributions, and a below-zero crash.
 */

/** @type {{ accountType: 'Investment Account' }} */
const ACCOUNT = { accountType: 'Investment Account' };

const LOCALES = {
	default: { scale: 1, symbol: '\u00A3', decimalPlaces: 2, url: 'app.thyme.money/accounts/isa' },
	en: { scale: 1, symbol: '\u00A3', decimalPlaces: 2, url: 'app.thyme.money/accounts/isa' },
	'en-US': { scale: 1, symbol: '$', decimalPlaces: 2, url: 'app.thyme.money/accounts/brokerage' },
	ja: { scale: 100, symbol: '\u00A5', decimalPlaces: 0, url: 'app.thyme.money/accounts/nisa' },
	ko: { scale: 1000, symbol: '\u20A9', decimalPlaces: 0, url: 'app.thyme.money/accounts/growth-fund' },
	'zh-CN': { scale: 7, symbol: '\u00A5', decimalPlaces: 2, url: 'app.thyme.money/accounts/growth-fund' },
	'zh-TW': { scale: 40, symbol: 'NT$', decimalPlaces: 0, url: 'app.thyme.money/accounts/growth-fund' },
};

/**
 * @param {string} lang
 * @returns {{
 *   account: { accountType: 'Investment Account' },
 *   history: Array<{ date: string, value: number }>,
 *   valuations: Array<{ date: string, value: number }>,
 *   balance: number,
 *   principal: number,
 *   symbol: string,
 *   decimalPlaces: number,
 *   url: string,
 * }}
 */
/**
 * @param {string} lang
 * @returns {{ scale: number, symbol: string, decimalPlaces: number, url: string }}
 */
function localeConfig(lang) {
	const key = /** @type {keyof typeof LOCALES} */ (lang);
	return LOCALES[key] || LOCALES.default;
}

/**
 * @param {string} lang
 * @returns {{
 *   account: { accountType: 'Investment Account' },
 *   history: Array<{ date: string, value: number }>,
 *   valuations: Array<{ date: string, value: number }>,
 *   balance: number,
 *   principal: number,
 *   symbol: string,
 *   decimalPlaces: number,
 *   url: string,
 * }}
 */
export function getVisualiseMockData(lang) {
	const { scale, symbol, decimalPlaces, url } = localeConfig(lang);

	/**
	 * @param {number} v
	 * @returns {number}
	 */
	function s(v) { return v * scale; }

	return {
		account: ACCOUNT,
		history: [
			{ date: '2024-03-01', value: s(20000) },
			{ date: '2024-06-01', value: s(38000) },
			{ date: '2024-11-01', value: s(43000) },
			{ date: '2025-02-01', value: s(68000) },
			{ date: '2025-06-01', value: s(73000) },
			{ date: '2025-09-01', value: s(83000) },
			{ date: '2026-03-01', value: s(90000) },
		],
		valuations: [
			{ date: '2024-03-01', value: s(20000) },
			{ date: '2024-04-01', value: s(21300) },
			{ date: '2024-05-01', value: s(22400) },
			{ date: '2024-06-01', value: s(39500) },
			{ date: '2024-07-01', value: s(41800) },
			{ date: '2024-08-01', value: s(36200) },
			{ date: '2024-09-01', value: s(-2800) },
			{ date: '2024-10-01', value: s(24800) },
			{ date: '2024-11-01', value: s(44800) },
			{ date: '2024-12-01', value: s(46800) },
			{ date: '2025-01-01', value: s(38500) },
			{ date: '2025-02-01', value: s(71200) },
			{ date: '2025-03-01', value: s(74500) },
			{ date: '2025-04-01', value: s(59800) },
			{ date: '2025-05-01', value: s(65500) },
			{ date: '2025-06-01', value: s(77200) },
			{ date: '2025-07-01', value: s(81000) },
			{ date: '2025-08-01', value: s(70200) },
			{ date: '2025-09-01', value: s(89500) },
			{ date: '2025-10-01', value: s(95800) },
			{ date: '2025-11-01', value: s(81400) },
			{ date: '2025-12-01', value: s(87500) },
			{ date: '2026-01-01', value: s(96500) },
			{ date: '2026-02-01', value: s(110200) },
			{ date: '2026-03-01', value: s(124800) },
			{ date: '2026-04-01', value: s(141500) },
			{ date: '2026-05-01', value: s(128300) },
			{ date: '2026-06-01', value: s(152000) },
			{ date: '2026-07-01', value: s(176400) },
			{ date: '2026-08-01', value: s(203500) },
			{ date: '2026-09-01', value: s(224568) },
		],
		balance: s(224568),
		principal: s(90000),
		symbol,
		decimalPlaces,
		url,
	};
}
